import { FormInstance } from 'antd';
import dayjs from 'dayjs';
import { MemberRank, PaymentType, ProductAttribute, ProductVersion, SaleItemDimension, Unit } from 'models';
import { DiscountCode } from 'models/DiscountCode';
import { Material } from 'models/Material';
import Staff from 'models/Staff';
import { BaseRecord, FileData, Gender, ImagePayload } from 'types';

export interface FilterOption {
  value: string;
  label: string;
}

export interface ReportFilterHeaderProps {
  reportTypes?: FilterOption[];
  selectedReportType?: 'orders' | 'revenue';
  onReportTypeChange?: (value: 'orders' | 'revenue') => void;
  onExportData?: () => void;
  onApplyFilter?: () => void;
  onClearFilter?: () => void;
  className?: string;
  selectedDateRangeValue?: [dayjs.Dayjs, dayjs.Dayjs] | null;
  onDateRangeValueChange?: (value: [dayjs.Dayjs, dayjs.Dayjs] | null) => void;
}

export interface OrderData {
  date: string;
  returnOrders: number;
  saleOrders: number;
  purchaseOrders: number;
}

export interface ColumnChartProps {
  title: string;
  data: OrderData[];
  className?: string;
  onExpandClick?: () => void;
  yAxisSuffix?: string;
  type?: string;
}
export interface ChartData {
  name: string;
  value: number;
  percentage: number;
  color: string;
  displayValue: string; // For formatted display (e.g., "3,491,000" or "8")
  [key: string]: string | number;
}

export interface CircularChartProps {
  title: string;
  centerLabel: string;
  centerValue: string; // For formatted display (e.g., "10M2" or "108")
  data: ChartData[];
  className?: string;
  onExpandClick?: () => void;
}
export interface UploadFileCommonProps {
  setFilesData: React.Dispatch<React.SetStateAction<{ id?: string; filePath: string; order: number; name: string }[]>>;
  isDisabled?: boolean;
  onNewFileUpload?: (filePath: string, fileName: string) => void;
  onRemoveFile?: (fileId: string) => void;
  filesData: { id?: string; filePath: string; order: number; name: string }[];
  accept?: string;
  maxSizeMB?: number;
  maxCount?: number;
  readOnly?: boolean;
}
export interface UploadImageCommonProps {
  setImagesData: React.Dispatch<React.SetStateAction<{ filePath: string; order: number; name: string }[]>>;
  isDisabled: boolean;
  imagesData: ImagePayload[];
}
export interface StaffRecord extends BaseRecord<unknown> {
  key: string;
  id: string;
  ['baseUser.code']: string;
  ['baseUser.name']: string;
  ['baseUser.email']: string;
  ['baseUser.phoneNumber']: string;
  gender?: Gender;
  roleName: string;
  ['baseUser.isActive']?: boolean;
  ['baseUser.id']: string;
  password: string;
  roleId?: string;
}
export interface MemberRankRecord extends BaseRecord<unknown> {
  key: string;
  id: string;
  name: string;
  description?: string;
  minPoints: number;
  maxPoints: number;
  isActive: boolean;
  color?: string;
  discount?: number;
}
export interface MaterialRecord extends BaseRecord<unknown> {
  key: string;
  id: string;
  code: string;
  name: string;
  costPrice: number;
  salePrice: number;
  description?: string;
  quantity: number;
  createdAt: Date;
  updatedAt: Date;
}
export interface OrderStats {
  totalSales: number;
  totalPurchase: number;
}

export interface RevenueStats {
  totalSales: number;
  totalDebts: number;
  totalPurchase: number;
}

export interface PaymentMethodStats {
  totalCash: number;
  totalTransfer: number;
}
export interface AddMaterialModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: Partial<Material>) => void;
  onGenerate?: () => Promise<string>;
  form: FormInstance;
  editMode?: boolean;
  materialData?: MaterialRecord | null;
  isSubmitting?: boolean;
  isCopyMode?: boolean;
}
export interface AddDiscountCodeModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: Partial<DiscountCode>) => void;
  onGenerate?: () => Promise<string>;
  form: FormInstance;
  editMode?: boolean;
  discountCodeData?: DiscountCodeRecord | null;
  isSubmitting?: boolean;
}
export interface AddStaffModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: Partial<Staff>) => void;
  onGenerate?: () => Promise<string>;
  form: FormInstance;
  editMode?: boolean;
  staffData?: StaffRecord | null;
  isSubmitting?: boolean;
}
export interface ViewStaffModalProps {
  isOpen: boolean;
  onCancel: () => void;
  editMode?: boolean;
  staffData?: {
    baseUser: {
      code: string;
      name: string;
      email?: string;
      phoneNumber?: string;
      isActive?: boolean;
      address?: string;
    };
    roleId?: string;
    note?: string;
  } | null;
  isSubmitting?: boolean;
}
export interface EditMemberRankModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: Partial<MemberRank>) => void;
  form: FormInstance;
  memberRankData?: MemberRankRecord | null;
  isSubmitting?: boolean;
}
export interface DiscountCodeRecord extends BaseRecord<unknown> {
  key: string;
  id: string;
  code: string;
  discountCode: string;
  discountValue: number;
  description?: string;
  isActive: boolean;
}
export interface InfoFieldProps {
  label: string;
  value: React.ReactNode;
  isHighlight?: boolean;
  isDelayed?: boolean;
  isValueBold?: boolean;
  className?: string;
  editable?: boolean;
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: (value: string) => void;
  onCancel?: () => void;
  fieldName?: string;
}
export enum CartStatus {
  PRODUCT_SELECTION = 'PRODUCT_SELECTION',
  PAYMENT = 'PAYMENT',
  COMPLETED = 'COMPLETED',
}

export interface CartItem {
  id: string;
  productVersion: ProductVersion;
  name?: string;
  quantity: number;
  unitConvertValue?: number;
  unit: Unit;
  unitPrice: number;
  attribute?: ProductAttribute;
  totalPrice: number;
  saleItemDimesions?: SaleItemDimension[];
}

export interface Cart {
  id: string;
  name: string;
  status: CartStatus;
  items: CartItem[];
  totalAmount: number;
  discount?: number;
  tax?: number;
  code?: string;
  saleOrderId?: string;
  customer?: CustomerInput | null;
  note?: string;
  paymentData?: {
    paymentType: PaymentType;
    discountType: string;
    discountValue: number;
    vatType: string;
    vatValue: number;
    prepaidAmount: number;
    extraFee: number;
    finalAmount?: number;
    remainingAmount?: number;
    deposit?: number;
    createdAt?: Date;
  };
  transactionMethod?: string;
  transactionAttachments?: FileData[];
}

export type CustomerInput = {
  id?: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
};

export interface CartState {
  carts: Cart[];
  activeCartId: string | null;
}

export type CartAction =
  | { type: 'ADD_CART'; payload: { name: string } }
  | { type: 'REMOVE_CART'; payload: { id: string } }
  | { type: 'SET_ACTIVE_CART'; payload: { id: string } }
  | { type: 'UPDATE_CART_STATUS'; payload: { id: string; status: CartStatus } }
  | { type: 'ADD_ITEM'; payload: { cartId: string; item: Omit<CartItem, 'id'> } }
  | { type: 'UPDATE_ITEM'; payload: { cartId: string; itemId: string; updates: Partial<CartItem> } }
  | { type: 'REMOVE_ITEM'; payload: { cartId: string; itemId: string } }
  | { type: 'UPDATE_CUSTOMER'; payload: { cartId: string; customer: Cart['customer'] } }
  | { type: 'UPDATE_CART_DISCOUNT'; payload: { cartId: string; discount: number } }
  | { type: 'CLEAR_CART'; payload: { cartId: string } }
  | { type: 'UPDATE_CART'; payload: { cartId: string; updates: Partial<Cart> } }
  | { type: 'RESET_ALL' };

export interface CartPOSContextType {
  state: CartState;
  addCart: (name: string) => void;
  removeCart: (id: string) => void;
  setActiveCart: (id: string) => void;
  updateCartStatus: (id: string, status: CartStatus) => void;
  addItem: (
    productVersion: ProductVersion,
    unit: Unit,
    quantity: number,
    unitPrice?: number,
    attribute?: ProductAttribute,
    cartId?: string,
    saleItemDimensions?: SaleItemDimension[],
  ) => void;
  updateItem: (itemId: string, updates: Partial<CartItem>) => void;
  removeItem: (itemId: string) => void;
  updateCustomer: (customer: Cart['customer'], cartId?: string) => void;
  updateDiscount: (discount: number) => void;
  updateCart: (cartId: string, updates: Partial<Cart>) => void;
  clearCart: () => void;
  resetAll: () => void;
  getActiveCart: () => Cart | null;
}
export interface ReturnOrderRecord extends BaseRecord<unknown> {
  id: string;
  key: string;

  code: string;
  'supplier.baseUser.name': string;
  createdAt: Date;
  deliveryDate: string;
  'status.code': string;
  count: number;
}
